import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { apiConfig } from '../config/environment';
import {
  LoginResponse,
  LoginCredentials,
  User,
  ApiLoginResponse,
} from '../types';
import { humanizeForeignKeyDeleteError } from '../utils/humanizeDbError';

// Configuração base da API
const API_BASE_URL = apiConfig.baseURL;

// Interface para dados de login (mantida para compatibilidade)
export interface LoginData {
  email: string;
  password: string;
}

/**
 * Anexa as notas fiscais ao FormData.
 *
 * Vários arquivos precisam ir como `nota_fiscal[]`: repetindo a chave sem
 * colchetes o PHP guarda apenas o último arquivo enviado.
 */
function appendNotaFiscalFiles(formData: FormData, value: any): void {
  if (!value) return;

  const list = (Array.isArray(value) ? value : [value])
    .map((item: any) => (item?.file instanceof File ? item.file : item))
    .filter((file: any): file is File => file instanceof File);

  if (list.length === 0) return;

  const field = list.length > 1 ? 'nota_fiscal[]' : 'nota_fiscal';
  list.forEach(file => formData.append(field, file));
}

/**
 * Serializa um item da remessa para o JSON enviado no FormData.
 * chave_acesso e document_id precisam ir junto — senão a extração da NF
 * mostra a chave na tela e o produto é gravado sem ela.
 */
function mapBoxItemJson(item: any, includeId = false): Record<string, string> {
  const itemData: Record<string, string> = {};

  if (includeId && item?.id !== undefined && item.id !== null && item.id !== '') {
    itemData.id = String(item.id);
  }
  if (item?.nome && String(item.nome).trim() !== '') {
    itemData.nome = String(item.nome);
  }
  if (item?.ncm != null && String(item.ncm).trim() !== '') {
    itemData.ncm = String(item.ncm);
  }
  if (item?.cfop != null && String(item.cfop).trim() !== '') {
    itemData.cfop = String(item.cfop);
  }
  if (item?.descricao && String(item.descricao).trim() !== '') {
    itemData.descricao = String(item.descricao);
  }
  if (item?.quantidade !== undefined && item.quantidade !== null && item.quantidade !== '') {
    itemData.quantidade = String(item.quantidade);
  }
  if (item?.peso !== undefined && item.peso !== null && item.peso !== '') {
    itemData.peso = String(item.peso);
  }
  if (item?.preco !== undefined && item.preco !== null && item.preco !== '') {
    itemData.preco = String(item.preco);
  }

  const chave = String(item?.chave_acesso ?? '').replace(/\D/g, '');
  if (chave) {
    itemData.chave_acesso = chave;
  }
  if (
    item?.document_id != null &&
    item.document_id !== '' &&
    /^\d+$/.test(String(item.document_id))
  ) {
    itemData.document_id = String(item.document_id);
  }

  if (includeId) {
    const manter = (Array.isArray(item?.fotos) ? item.fotos : [])
      .filter(
        (photo: any) =>
          photo &&
          !(photo.file instanceof File) &&
          photo.id != null &&
          /^\d+$/.test(String(photo.id))
      )
      .map((photo: any) => String(photo.id));
    itemData.fotos_manter = manter.join(',');

    const remover = (Array.isArray(item?.fotos_remover)
      ? item.fotos_remover
      : String(item?.fotos_remover || '').split(','))
      .map((id: any) => String(id).trim())
      .filter((id: string) => /^\d+$/.test(id));
    if (remover.length > 0) {
      itemData.fotos_remover = Array.from(new Set(remover)).join(',');
    }
  }

  return itemData;
}

// Classe para gerenciar a API
class ApiService {
  private api: AxiosInstance;
  private refreshInFlight: Promise<{ token: string; user: any }> | null = null;

  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Interceptor para adicionar token de autenticação
    this.api.interceptors.request.use(
      config => {
        const token = localStorage.getItem('token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      error => {
        return Promise.reject(error);
      }
    );

    // Interceptor para tratar erros de resposta
    this.api.interceptors.response.use(
      (response: AxiosResponse) => response,
      error => {
        if (error.response?.status === 401) {
          // Não fazer logout se for alteração de senha - manter usuário logado
          const isChangePassword = error.config?.url?.includes('/usuario/change-password');
          // Não fazer logout se estiver na página de login (erro de credenciais)
          const isLoginPage = window.location.pathname === '/login' || error.config?.url?.includes('/login');
          // Não fazer logout se estiver na página de rastreamento (página pública)
          const isPublicPage =
            window.location.pathname === '/rastreamento' ||
            window.location.pathname === '/retirada' ||
            window.location.pathname.startsWith('/pagar/');
          const isPublicApi =
            error.config?.url?.includes('/verify') ||
            error.config?.url?.includes('/pacote/verificar-cliente') ||
            error.config?.url?.includes('/pacote/reportar-retirada') ||
            error.config?.url?.includes('/pendencias-pagamento') ||
            error.config?.url?.includes('/cobrancas/retirada') ||
            error.config?.url?.includes('/tracking_client') ||
            (error.config?.url?.includes('/cobrancas/') &&
              error.config?.url?.includes('/public'));
          const isTrackingPage = isPublicPage || isPublicApi;

          if (!isChangePassword && !isLoginPage && !isTrackingPage) {
            // Token expirado ou inválido
            console.log(
              'Token expirado ou inválido, redirecionando para login...'
            );
            this.clearAuthData();

            // Redirecionar para login apenas se não estiver já na página de login
            if (window.location.pathname !== '/login') {
              window.location.href = '/login';
            }
          }
        }
        return Promise.reject(error);
      }
    );
  }

  // Método para fazer login
  async login(data: LoginCredentials): Promise<LoginResponse> {
    try {
      const response = await this.api.post<ApiLoginResponse>(
        '/api/v1/login',
        data
      );

      // Extrair o token da resposta
      const { authorisation, user } = response.data;

      if (authorisation?.token) {
        // Armazenar o token no localStorage
        localStorage.setItem('token', authorisation.token);

        // Armazenar dados do usuário
        localStorage.setItem('user', JSON.stringify(user));

        // Retornar dados do usuário (sem o token por segurança)
        return { user, refresh_token: response.data.refresh_token };
      } else {
        throw new Error('Token de autorização não encontrado na resposta');
      }
    } catch (error: any) {
      // Tratar especificamente erros 401/Unauthorized
      if (error.response?.status === 401 || error.response?.data?.status === 'error') {
        const message = error.response?.data?.message || 'Credenciais inválidas. Verifique seu email e senha.';
        throw new Error(message);
      }
      throw new Error(error.response?.data?.message || error.message || 'Erro ao fazer login');
    }
  }

  // Método para fazer logout
  async logout(): Promise<void> {
    try {
      await this.api.post('/api/v1/logout');
    } catch (error) {
      console.error('Erro ao fazer logout:', error);
    } finally {
      // Sempre limpar dados locais
      this.clearAuthData();
    }
  }

  // Método para limpar dados de autenticação
  clearAuthData(): void {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    console.log('Dados de autenticação limpos do localStorage');
  }

  // Método para forçar logout e limpeza completa
  forceLogout(): void {
    this.clearAuthData();

    // Redirecionar para login se não estiver já lá
    if (window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
  }

  // Método para verificar se o usuário está autenticado
  isAuthenticated(): boolean {
    try {
      const token = localStorage.getItem('token');
      const user = localStorage.getItem('user');

      // Verificar se existe token e usuário válido
      if (!token || !user) {
        return false;
      }

      // Verificar se o usuário é válido
      const parsedUser = JSON.parse(user);
      if (!parsedUser || !parsedUser.id || !parsedUser.email) {
        // Limpar dados inválidos
        this.clearAuthData();
        return false;
      }

      // Retornar true se há token e usuário válido
      return true;
    } catch (error) {
      console.error('Erro ao verificar autenticação:', error);
      // Em caso de erro, limpar dados corrompidos
      this.clearAuthData();
      return false;
    }
  }

  // Método para verificar autenticação real com a API
  async checkAuthenticationWithAPI(): Promise<boolean> {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        return false;
      }

      // Tentar fazer uma requisição para verificar se o token ainda é válido
      const response = await this.api.get('/api/v1/profile');
      return response.status === 200;
    } catch (error) {
      // Se houver erro (API offline, token expirado, etc.), limpar dados e retornar false
      console.error('Erro ao verificar autenticação com API:', error);
      this.clearAuthData();
      return false;
    }
  }

  // Método para obter o token atual
  getToken(): string | null {
    return localStorage.getItem('token');
  }

  // Método para decodificar JWT e obter a data de expiração
  getTokenExpiration(token: string): number | null {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => {
            const hex = c.charCodeAt(0).toString(16);
            const paddedHex = `00${hex}`.slice(-2);
            return `%${paddedHex}`;
          })
          .join('')
      );
      const decoded = JSON.parse(jsonPayload);
      return decoded.exp ? decoded.exp * 1000 : null; // Converter para milissegundos
    } catch (error) {
      console.error('Erro ao decodificar token:', error);
      return null;
    }
  }

  // Método para renovar o token. Uma renovação por vez.
  async refreshToken(): Promise<{ token: string; user: any }> {
    if (this.refreshInFlight) {
      return this.refreshInFlight;
    }
    this.refreshInFlight = this.requestRefresh().finally(() => {
      this.refreshInFlight = null;
    });
    return this.refreshInFlight;
  }

  private async requestRefresh(): Promise<{ token: string; user: any }> {
    try {
      const response = await this.api.post('/api/v1/refresh');
      const { authorisation, user } = response.data;

      if (authorisation?.token) {
        // Atualizar o token no localStorage
        localStorage.setItem('token', authorisation.token);

        // Atualizar dados do usuário se fornecidos
        if (user) {
          localStorage.setItem('user', JSON.stringify(user));
        }

        return { token: authorisation.token, user };
      } else {
        throw new Error('Token de autorização não encontrado na resposta');
      }
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erro ao renovar token');
    }
  }

  // Método para obter dados do usuário atual
  getCurrentUser(): User | null {
    try {
      const user = localStorage.getItem('user');
      if (!user) return null;

      const parsedUser = JSON.parse(user);

      // Verificar se o usuário tem as propriedades necessárias
      if (
        parsedUser &&
        typeof parsedUser === 'object' &&
        parsedUser.id &&
        parsedUser.email
      ) {
        return parsedUser;
      }

      // Se o usuário não for válido, limpar o localStorage
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      return null;
    } catch (error) {
      console.error('Erro ao obter usuário atual:', error);
      // Em caso de erro, limpar dados corrompidos
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      return null;
    }
  }

  // Método para buscar perfil do usuário da API
  async getProfile(): Promise<User> {
    try {
      console.log('🔍 Fazendo requisição para /api/v1/profile...');
      const response = await this.api.get('/api/v1/profile');

      // A API retorna { status, message, data }
      // Se response.data tem a propriedade 'data', usar ela, senão usar response.data diretamente
      const user = response.data?.data || response.data;

      console.log('📊 Resposta da API:', response);
      console.log('👤 Dados do usuário recebidos:', user);
      console.log('🔑 Estrutura permissao:', user.permissao);
      console.log('🔑 Permissão ID recebido:', user.permissao?.id);
      console.log('🔑 Permissão permission:', user.permissao?.permission);

      // Atualizar dados no localStorage
      localStorage.setItem('user', JSON.stringify(user));

      return user;
    } catch (error: any) {
      console.error('❌ Erro na API getProfile:', error);
      throw new Error(
        error.response?.data?.message || 'Erro ao buscar perfil do usuário'
      );
    }
  }

  // Método para fazer requisições autenticadas
  async authenticatedRequest<T>(config: any): Promise<T> {
    try {
      const response = await this.api.request<T>(config);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erro na requisição');
    }
  }

  // Método para solicitar recuperação de senha
  async forgotPassword(email: string): Promise<void> {
    try {
      await this.api.post('/api/v1/security/esquecer-senha', { email });
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message ||
          'Erro ao solicitar recuperação de senha'
      );
    }
  }

  // Método para verificar código de recuperação
  async checkRecoveryCode(email: string, code: string): Promise<string> {
    try {
      const response = await this.api.post('/api/v1/security/check-code', {
        email,
        code,
      });

      // Retornar o token gerado pela validação do código
      const token = response.data?.token || response.data?.authorisation?.token;

      if (!token) {
        throw new Error('Token não encontrado na resposta do servidor');
      }

      return token;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Código de recuperação inválido'
      );
    }
  }

    // Método para redefinir senha
  async resetPassword(
    email: string,
    code: string,
    token: string,
    password: string
  ): Promise<void> {
    try {
      console.log('Enviando requisição para resetPassword:', {
        email,
        code,
        token: '***',
        password: '***',
      });

      const response = await this.api.post('/api/v1/security/nova-senha', {
        email,
        code,
        token,
        password,
      });

      console.log('Resposta do servidor:', response.data);
    } catch (error: any) {
      console.error('Erro completo na API:', error);
      console.error('Response data:', error.response?.data);
      console.error('Response status:', error.response?.status);

      let errorMessage = 'Erro ao redefinir senha';

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.status === 400) {
        errorMessage = 'Dados inválidos enviados para o servidor';
      } else if (error.response?.status === 401) {
        errorMessage = 'Código de verificação inválido ou expirado';
      } else if (error.response?.status === 404) {
        errorMessage = 'Serviço de recuperação não encontrado';
      } else if (error.response?.status === 500) {
        errorMessage = 'Erro interno do servidor';
      }

      throw new Error(errorMessage);
    }
  }

  // Método para atualizar perfil
  async updateProfile(data: {
    nome?: string;
    telefone?: string;
    endereco?: string;
  }): Promise<User> {
    try {
      const response = await this.api.put('/api/v1/profile', data);
      // A API pode retornar { status, message, data } ou diretamente o user
      const user = response.data?.data || response.data;
      localStorage.setItem('user', JSON.stringify(user));
      return user;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao atualizar perfil'
      );
    }
  }

  async updateAvatar(avatar: File): Promise<User> {
    try {
      const formData = new FormData();
      formData.append('avatar', avatar);

      const response = await this.api.post('/api/v1/profile/avatar', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      // A API pode retornar { status, message, data } ou diretamente o user
      const userData = response.data?.data || response.data;

      // Atualizar dados no localStorage
      localStorage.setItem('user', JSON.stringify(userData));

      return userData;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao atualizar avatar'
      );
    }
  }

  async getProfileStats(): Promise<any> {
    try {
      const response = await this.api.get('/api/v1/profile/stats');
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao buscar estatísticas do perfil'
      );
    }
  }

  async changePassword(data: {
    current_password: string;
    password: string;
    password_confirmation: string;
  }): Promise<any> {
    try {
      const response = await this.api.post('/api/v1/usuario/change-password', data);
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao alterar senha'
      );
    }
  }

  // ========== DASHBOARD ==========
  async getDomainRealtimeStream(params: {
    since: number;
    topics?: string[];
  }): Promise<{
    status?: string;
    events?: Array<{
      id: string;
      event: string;
      topic?: string;
      action?: string;
      payload?: Record<string, unknown>;
      at: number;
    }>;
    serverTime?: number;
  }> {
    const response = await this.api.get('/api/v1/realtime/stream', {
      params: {
        since: params.since,
        ...(params.topics?.length ? { topics: params.topics.join(',') } : {}),
      },
      timeout: 15000,
    });
    return response.data;
  }

  async getDashboardSummary(): Promise<any> {
    try {
      const response = await this.api.get('/api/v1/dashboard/summary');
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao buscar resumo do dashboard'
      );
    }
  }

  async getClients(params?: {
    search?: string;
    status?: string;
  }): Promise<any> {
    try {
      const response = await this.api.get('/api/v1/clients', { params });
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao buscar clientes'
      );
    }
  }

  async getInvoices(params?: { status?: string }): Promise<any> {
    try {
      const response = await this.api.get('/api/v1/invoices', { params });
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao buscar invoices'
      );
    }
  }

  async getLocations(): Promise<any> {
    try {
      const response = await this.api.get('/api/v1/locations');
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao buscar localizações'
      );
    }
  }

  // ========== CLIENTES ==========
  async getClientsList(params?: {
    search?: string;
    status?: string;
    page?: number;
    per_page?: number;
  }): Promise<any> {
    try {
      const response = await this.api.get('/api/v1/clients', { params });
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao buscar clientes'
      );
    }
  }

  async createClient(data: {
    nome: string;
    email: string;
    telefone?: string;
    documento?: string;
    endereco?: string;
    status?: string;
  }): Promise<any> {
    try {
      const response = await this.api.post('/api/v1/clients', data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erro ao criar cliente');
    }
  }

  async updateClient(
    id: number,
    data: Partial<{
      nome: string;
      email: string;
      telefone: string;
      documento: string;
      endereco: string;
      status: string;
    }>
  ): Promise<any> {
    try {
      const response = await this.api.patch(`/api/v1/clients/${id}`, data);
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao atualizar cliente'
      );
    }
  }

  async deleteClient(id: number): Promise<void> {
    try {
      await this.api.delete(`/api/v1/clients/${id}`);
    } catch (error: any) {
      const raw =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        '';
      const message = humanizeForeignKeyDeleteError(
        raw || 'Erro ao excluir cliente',
        'este cliente'
      );
      throw new Error(message);
    }
  }

  // ========== USUÁRIOS ==========
  async getUsers(params?: {
    search?: string;
    status?: string;
    role?: string;
    page?: number;
    per_page?: number;
  }): Promise<any> {
    try {
      const response = await this.api.get('/api/v1/usuario/listar', { params });
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao buscar usuários'
      );
    }
  }

  async createUser(data: {
    nome: string;
    email: string;
    password: string;
    telefone?: string;
    permissaos_id: number;
    department?: string;
    status?: string;
  }): Promise<any> {
    try {
      const response = await this.api.post('/api/v1/usuario/cadastro', data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erro ao criar usuário');
    }
  }

  async updateUser(
    id: number,
    data: Partial<{
      nome: string;
      email: string;
      telefone?: string;
      permissaos_id: number;
      department: string;
      status: string;
    }>
  ): Promise<any> {
    try {
      const response = await this.api.put(`/api/v1/users/${id}`, data);
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao atualizar usuário'
      );
    }
  }

  async deleteUser(id: number): Promise<void> {
    try {
      await this.api.delete(`/api/v1/users/${id}`);
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao excluir usuário'
      );
    }
  }

  // ========== PERMISSÕES ==========
  async getPermissaos(): Promise<any> {
    try {
      const response = await this.api.get('/api/v1/permissaos');
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao buscar permissões'
      );
    }
  }

  async createPermissao(data: {
    permissao: string;
    nome: string;
    descricao?: string;
  }): Promise<any> {
    try {
      const response = await this.api.post('/api/v1/permissaos', data);
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao criar permissão'
      );
    }
  }

  async updatePermissao(
    id: number,
    data: {
      permissao: string;
      nome: string;
      descricao?: string;
    }
  ): Promise<any> {
    try {
      const response = await this.api.put(`/api/v1/permissaos/${id}`, data);
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao atualizar permissão'
      );
    }
  }

  async deletePermissao(id: number): Promise<any> {
    try {
      const response = await this.api.delete(`/api/v1/permissaos/${id}`);
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao excluir permissão'
      );
    }
  }

  // ========== PACOTES ==========
  async getPackages(params?: {
    search?: string;
    status?: string;
    year?: string;
    page?: number;
    per_page?: number;
  }): Promise<any> {
    try {
      const response = await this.api.get('/api/v1/packages', { params });
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao buscar pacotes'
      );
    }
  }

  async createPackage(data: any): Promise<any> {
    try {
      const formData = new FormData();
      Object.keys(data).forEach(key => {
        if (key === 'items') {
          // Enviar items como JSON string
          formData.append(key, JSON.stringify(data[key]));
        } else if (key === 'purchaseInvoice' && data[key]) {
          // Se for File, enviar diretamente
          if (data[key] instanceof File) {
            formData.append(key, data[key]);
          }
        } else if (key === 'fotos' && Array.isArray(data[key])) {
          // Enviar múltiplas fotos
          data[key].forEach((foto: File | string, index: number) => {
            if (foto instanceof File) {
              formData.append(`fotos[${index}]`, foto);
            } else if (typeof foto === 'string') {
              // Se for base64, enviar como string
              formData.append(`fotos[${index}]`, foto);
            }
          });
        } else if (key === 'codigos_acesso') {
          // Enviar codigos_acesso como JSON string (apenas cliente agora)
          // Formato esperado: { cliente: "CODIGO123" }
          if (typeof data[key] === 'object') {
            formData.append(key, JSON.stringify(data[key]));
          } else {
            formData.append(key, data[key]);
          }
        } else if (
          data[key] !== null &&
          data[key] !== undefined &&
          data[key] !== ''
        ) {
          // Converter números para string
          if (typeof data[key] === 'number') {
            formData.append(key, String(data[key]));
          } else {
            formData.append(key, data[key]);
          }
        }
      });

      const response = await this.api.post('/api/v1/packages', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erro ao criar pacote');
    }
  }

  async updatePackage(id: number, data: any): Promise<any> {
    try {
      const formData = new FormData();
      Object.keys(data).forEach(key => {
        if (key === 'items') {
          // Enviar items como JSON string
          formData.append(key, JSON.stringify(data[key]));
        } else if (key === 'purchaseInvoice' && data[key]) {
          // Se for File, enviar diretamente
          if (data[key] instanceof File) {
            formData.append(key, data[key]);
          }
        } else if (key === 'fotos' && Array.isArray(data[key])) {
          // Enviar múltiplas fotos
          data[key].forEach((foto: File | string, index: number) => {
            if (foto instanceof File) {
              formData.append(`fotos[${index}]`, foto);
            } else if (typeof foto === 'string') {
              // Se for base64, enviar como string
              formData.append(`fotos[${index}]`, foto);
            }
          });
        } else if (key === 'codigos_acesso') {
          // Enviar codigos_acesso como JSON string (apenas cliente agora)
          // Formato esperado: { cliente: "CODIGO123" }
          if (typeof data[key] === 'object') {
            formData.append(key, JSON.stringify(data[key]));
          } else {
            formData.append(key, data[key]);
          }
        } else if (
          data[key] !== null &&
          data[key] !== undefined &&
          data[key] !== ''
        ) {
          // Converter números para string
          if (typeof data[key] === 'number') {
            formData.append(key, String(data[key]));
          } else {
            formData.append(key, data[key]);
          }
        }
      });

      const response = await this.api.patch(
        `/api/v1/packages/${id}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao atualizar pacote'
      );
    }
  }

  async deletePackage(id: number): Promise<void> {
    try {
      await this.api.delete(`/api/v1/packages/${id}`);
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao excluir pacote'
      );
    }
  }

  /**
   * Extrai chave e itens de uma NF (PDF/imagem) sem salvar o pacote.
   */
  async extractNotaFiscal(file: File): Promise<{
    status: string;
    message?: string;
    data?: {
      chave_acesso?: string | null;
      itens?: Array<Record<string, unknown>>;
    };
  }> {
    try {
      const formData = new FormData();
      formData.append('nota_fiscal', file);
      const response = await this.api.post(
        '/api/v1/packages/extract-nota-fiscal',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          // PDF escaneado + OCR pode passar de 1 min; default do axios abortava.
          timeout: 180000,
        }
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message ||
          'Erro ao extrair dados da nota fiscal'
      );
    }
  }

  async getPackage(id: number): Promise<any> {
    try {
      const response = await this.api.get(`/api/v1/packages/${id}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erro ao buscar pacote');
    }
  }

  // ========== INVOICES ==========
  async getInvoice(id: number): Promise<any> {
    try {
      const response = await this.api.get(`/api/v1/invoices/${id}`);
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao buscar invoice'
      );
    }
  }

  async getInvoicesList(params?: {
    status?: string;
    search?: string;
    page?: number;
    per_page?: number;
    arquivadas?: boolean | number;
    incluir_arquivadas?: boolean | number;
  }): Promise<any> {
    try {
      const response = await this.api.get('/api/v1/invoices', { params });
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao buscar invoices'
      );
    }
  }

  async createInvoice(data: any): Promise<any> {
    try {
      const response = await this.api.post('/api/v1/invoices', data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erro ao criar invoice');
    }
  }

  async updateInvoice(id: number, data: any): Promise<any> {
    try {
      const response = await this.api.put(`/api/v1/invoices/${id}`, data);
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao atualizar invoice'
      );
    }
  }

  async deleteInvoice(id: number): Promise<void> {
    try {
      await this.api.delete(`/api/v1/invoices/${id}`);
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao excluir invoice'
      );
    }
  }

  async archiveInvoice(id: number): Promise<any> {
    try {
      const response = await this.api.post(`/api/v1/invoices/${id}/arquivar`);
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao arquivar fatura'
      );
    }
  }

  async getInvoiceStatus(): Promise<any> {
    try {
      const response = await this.api.get('/api/v1/invoice/status');
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao buscar status de invoice'
      );
    }
  }

  // ========== FORNECEDORES ==========
  async getSuppliers(params?: {
    search?: string;
    status?: string;
    category?: string;
    page?: number;
    per_page?: number;
  }): Promise<any> {
    try {
      const response = await this.api.get('/api/v1/suppliers', { params });
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao buscar fornecedores'
      );
    }
  }

  // Endpoint específico para listar fornecedores (usado em invoices)
  async getFornecedor(): Promise<any> {
    try {
      const response = await this.api.get('/api/v1/fornecedor');
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao buscar fornecedores'
      );
    }
  }

  async createSupplier(data: {
    razao_social: string;
    nome_fantasia?: string;
    email: string;
    telefone?: string;
    cnpj?: string;
    endereco?: string;
    cidade?: string;
    uf?: string;
    cep?: string;
    bairro?: string;
    numero?: string;
    complemento?: string;
    categoria: string;
    status?: string;
  }): Promise<any> {
    try {
      const response = await this.api.post('/api/v1/suppliers', data);
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao criar fornecedor'
      );
    }
  }

  async updateSupplier(
    id: number,
    data: Partial<{
      razao_social: string;
      nome_fantasia?: string;
      email: string;
      telefone: string;
      cnpj?: string;
      endereco?: string;
      cidade?: string;
      uf?: string;
      cep?: string;
      bairro?: string;
      numero?: string;
      complemento?: string;
      categoria: string;
      status: string;
    }>
  ): Promise<any> {
    try {
      const response = await this.api.patch(`/api/v1/suppliers/${id}`, data);
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao atualizar fornecedor'
      );
    }
  }

  async deleteSupplier(id: number): Promise<void> {
    try {
      await this.api.delete(`/api/v1/suppliers/${id}`);
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao excluir fornecedor'
      );
    }
  }

  // ========== CAIXAS E MALAS ==========
  async getBoxes(params?: {
    search?: string;
    tipo?: string;
    status?: string;
    page?: number;
    per_page?: number;
    /** Código da invoice (ex.: INV-2024-001) — backend pode mapear para codigo_invoice */
    codigo_invoice?: string;
    /** Alias em inglês / alguns controllers */
    invoice?: string;
    destino?: string;
    destination?: string;
  }): Promise<any> {
    try {
      const response = await this.api.get('/api/v1/packages', { params });
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao buscar caixas/malas'
      );
    }
  }

  async createBox(data: any): Promise<any> {
    try {
      const formData = new FormData();

      // Processar itens: enviar dados como JSON e fotos como binários separados
      if (data.itens && Array.isArray(data.itens) && data.itens.length > 0) {
        // Preparar array de itens sem as fotos (apenas dados)
        const itensData = data.itens.map((item: any) => mapBoxItemJson(item));

        // Enviar dados dos itens como JSON string
        formData.append('itens', JSON.stringify(itensData));

        // Enviar fotos dos itens como arquivos binários separados
        // Formato: itens_fotos[0][0], itens_fotos[0][1], itens_fotos[1][0], etc.
        data.itens.forEach((item: any, itemIndex: number) => {
          if (
            item.fotos &&
            Array.isArray(item.fotos) &&
            item.fotos.length > 0
          ) {
            let photoIndex = 0;
            item.fotos.forEach((photo: any) => {
              // Apenas enviar arquivos novos (File), não URLs existentes
              if (photo?.file instanceof File) {
                formData.append(
                  `itens_fotos[${itemIndex}][${photoIndex}]`,
                  photo.file
                );
                photoIndex++;
              } else if (photo instanceof File) {
                formData.append(
                  `itens_fotos[${itemIndex}][${photoIndex}]`,
                  photo
                );
                photoIndex++;
              }
            });
          }
        });
      }

      // Processar outros campos - TODOS devem ser enviados (conforme especificação)
      Object.keys(data).forEach(key => {
        if (key === 'itens') {
          // Já processado acima, pular
          return;
        } else if (key === 'dimensoes') {
          // No CREATE, dimensões podem ser enviadas como campos separados OU como JSON string (alternativa)
          // Se vier como objeto dimensoes, enviar como JSON string também
          if (typeof data[key] === 'object' && data[key] !== null) {
            formData.append(key, JSON.stringify(data[key]));
          } else if (typeof data[key] === 'string' && data[key].trim() !== '') {
            formData.append(key, data[key]);
          }
        } else if (
          key === 'comprimento' ||
          key === 'largura' ||
          key === 'altura'
        ) {
          // Dimensões como campos separados - enviar sempre (mesmo se vazio)
          if (
            data[key] !== null &&
            data[key] !== undefined &&
            data[key] !== ''
          ) {
            formData.append(key, String(data[key]));
          } else {
            formData.append(key, '');
          }
        } else if (key === 'codigos_acesso') {
          // Enviar codigos_acesso como JSON string
          if (typeof data[key] === 'object' && data[key] !== null) {
            formData.append(key, JSON.stringify(data[key]));
          } else if (typeof data[key] === 'string' && data[key].trim() !== '') {
            formData.append(key, data[key]);
          }
        } else if (key === 'nota_fiscal') {
          appendNotaFiscalFiles(formData, data[key]);
        } else {
          // Enviar TODOS os outros campos sempre (mesmo opcionais)
          // Converter null/undefined para string vazia quando necessário
          const value = data[key];
          if (value !== null && value !== undefined && value !== '') {
            formData.append(key, String(value));
          } else {
            // Para campos opcionais vazios, enviar string vazia
            formData.append(key, '');
          }
        }
      });

      const response = await this.api.post('/api/v1/packages', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao criar caixa/mala'
      );
    }
  }

  async updateBox(id: number, data: any): Promise<any> {
    try {
      const formData = new FormData();

      // Processar itens: enviar dados como JSON e fotos como binários separados
      if (Array.isArray(data.itens) && data.itens.length === 0) {
        formData.append('itens', '[]');
      }

      if (data.itens && Array.isArray(data.itens) && data.itens.length > 0) {
        // Preparar array de itens sem as fotos (apenas dados)
        const itensData = data.itens.map((item: any) => mapBoxItemJson(item, true));

        // Enviar dados dos itens como JSON string
        formData.append('itens', JSON.stringify(itensData));

        // Enviar fotos dos itens como arquivos binários separados
        // Formato: itens_fotos[0][0], itens_fotos[0][1], itens_fotos[1][0], etc.
        data.itens.forEach((item: any, itemIndex: number) => {
          if (
            item.fotos &&
            Array.isArray(item.fotos) &&
            item.fotos.length > 0
          ) {
            let photoIndex = 0;
            item.fotos.forEach((photo: any) => {
              // Apenas enviar arquivos novos (File), não URLs existentes
              if (photo?.file instanceof File) {
                formData.append(
                  `itens_fotos[${itemIndex}][${photoIndex}]`,
                  photo.file
                );
                photoIndex++;
              } else if (photo instanceof File) {
                formData.append(
                  `itens_fotos[${itemIndex}][${photoIndex}]`,
                  photo
                );
                photoIndex++;
              }
              // Se for URL string (foto já existente), não enviar novamente
            });
          }
        });
      }

      // Processar outros campos - TODOS os campos devem ser enviados (conforme especificação)
      Object.keys(data).forEach(key => {
        if (key === 'itens') {
          // Já processado acima, pular
          return;
        } else if (key === 'dimensoes') {
          // No UPDATE, dimensões podem ser enviadas como campos separados OU como JSON string
          // Se vier como objeto dimensoes, enviar como JSON string (conforme especificação)
          if (typeof data[key] === 'object' && data[key] !== null) {
            formData.append(key, JSON.stringify(data[key]));
          } else if (typeof data[key] === 'string') {
            // Se já for string (JSON), enviar diretamente
            formData.append(key, data[key]);
          }
        } else if (key === 'codigos_acesso') {
          // Enviar codigos_acesso como JSON string
          if (typeof data[key] === 'object' && data[key] !== null) {
            formData.append(key, JSON.stringify(data[key]));
          } else if (typeof data[key] === 'string') {
            // Se já for string (JSON), enviar diretamente
            formData.append(key, data[key]);
          }
        } else if (key === 'nota_fiscal') {
          appendNotaFiscalFiles(formData, data[key]);
        } else if (key === 'remover_nota_fiscal') {
          // Ids dos documentos de NF que devem ser apagados no servidor
          const ids = Array.isArray(data[key]) ? data[key] : [data[key]];
          const validos = ids.filter(
            (id: any) => id !== null && id !== undefined && id !== ''
          );
          if (validos.length > 0) {
            formData.append(key, JSON.stringify(validos));
          }
        } else {
          // Enviar TODOS os outros campos sempre (mesmo opcionais)
          // Converter null/undefined para string vazia quando necessário
          const value = data[key];
          if (value !== null && value !== undefined && value !== '') {
            formData.append(key, String(value));
          } else {
            // Para campos opcionais vazios, enviar string vazia
            formData.append(key, '');
          }
        }
      });

      const response = await this.api.put(`/api/v1/packages/${id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao atualizar caixa/mala'
      );
    }
  }

  async deleteBox(id: number): Promise<void> {
    try {
      await this.api.delete(`/api/v1/packages/${id}`);
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao excluir caixa/mala'
      );
    }
  }

  async deleteItemPhoto(packageId: number, fotoId: number): Promise<void> {
    try {
      await this.api.delete(`/api/v1/packages/${packageId}/item-fotos/${fotoId}`);
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao excluir a foto do item'
      );
    }
  }

  // ========== COLETAS (recurso dedicado; não usar /api/v1/packages) ==========
  async getColetas(params?: {
    search?: string;
    page?: number;
    per_page?: number;
    status?: string;
  }): Promise<any> {
    try {
      const response = await this.api.get('/api/v1/coletas', { params });
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao buscar coletas'
      );
    }
  }

  async createColeta(data: Record<string, unknown>): Promise<any> {
    try {
      const response = await this.api.post('/api/v1/coletas', data);
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao criar coleta'
      );
    }
  }

  async updateColeta(
    id: number | string,
    data: Record<string, unknown>
  ): Promise<any> {
    try {
      const response = await this.api.put(`/api/v1/coletas/${id}`, data);
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao atualizar coleta'
      );
    }
  }

  async deleteColeta(id: number | string): Promise<void> {
    try {
      await this.api.delete(`/api/v1/coletas/${id}`);
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao excluir coleta'
      );
    }
  }

  async getBoxCodes(id: number): Promise<any> {
    try {
      const response = await this.api.get(`/api/v1/packages/${id}/codes`);
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao buscar códigos de acesso'
      );
    }
  }

  // ========== DOCUMENTOS ==========
  async getDocuments(params?: {
    search?: string;
    tipo?: string;
    page?: number;
    per_page?: number;
  }): Promise<any> {
    try {
      const response = await this.api.get('/api/v1/documents', { params });
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao buscar documentos'
      );
    }
  }

  async createDocument(data: {
    tipo: string;
    descricao: string;
    arquivo: File;
  }): Promise<any> {
    try {
      const formData = new FormData();
      formData.append('tipo', data.tipo);
      formData.append('descricao', data.descricao);
      formData.append('arquivo', data.arquivo);

      const response = await this.api.post('/api/v1/documents', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao criar documento'
      );
    }
  }

  async getDocument(id: number): Promise<any> {
    try {
      const response = await this.api.get(`/api/v1/documents/${id}`, {
        responseType: 'blob',
      });
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao buscar documento'
      );
    }
  }

  async downloadDocument(id: number): Promise<Blob> {
    try {
      const response = await this.api.get(`/api/v1/documents/${id}/download`, {
        responseType: 'blob',
      });
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao baixar documento'
      );
    }
  }

  async deleteDocument(id: number): Promise<void> {
    try {
      await this.api.delete(`/api/v1/documents/${id}`);
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao excluir documento'
      );
    }
  }

  // ========== DOCUMENTAÇÃO ADUANEIRA ==========
  async getCustoms(params?: {
    search?: string;
    box_id?: number;
    status?: string;
    page?: number;
    per_page?: number;
  }): Promise<any> {
    try {
      const response = await this.api.get('/api/v1/customs', { params });
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao buscar documentação aduaneira'
      );
    }
  }

  async createCustom(data: any): Promise<any> {
    try {
      const response = await this.api.post('/api/v1/customs', data);
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao criar documentação aduaneira'
      );
    }
  }

  async updateCustom(id: number, data: any): Promise<any> {
    try {
      const response = await this.api.patch(`/api/v1/customs/${id}`, data);
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message ||
          'Erro ao atualizar documentação aduaneira'
      );
    }
  }

  async deleteCustom(id: number): Promise<void> {
    try {
      await this.api.delete(`/api/v1/customs/${id}`);
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message ||
          'Erro ao excluir documentação aduaneira'
      );
    }
  }

  // ========== ROTAS E TRANSPORTES ==========
  async getRoutes(params?: {
    search?: string;
    status?: string;
    tipo_transporte?: string;
    page?: number;
    per_page?: number;
  }): Promise<any> {
    try {
      const response = await this.api.get('/api/v1/routes', { params });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erro ao buscar rotas');
    }
  }

  async createRoute(data: any): Promise<any> {
    try {
      const response = await this.api.post('/api/v1/routes', data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erro ao criar rota');
    }
  }

  async updateRoute(id: number, data: any): Promise<any> {
    try {
      const response = await this.api.patch(`/api/v1/routes/${id}`, data);
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao atualizar rota'
      );
    }
  }

  async deleteRoute(id: number): Promise<void> {
    try {
      await this.api.delete(`/api/v1/routes/${id}`);
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erro ao excluir rota');
    }
  }

  // ========== RASTREAMENTO ==========
  async getTracking(code: string): Promise<any> {
    try {
      const response = await this.api.get(`/api/v1/tracking/${code}`);
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao buscar rastreamento'
      );
    }
  }

  async getTrackingByClientCode(codigoCliente: string): Promise<any> {
    try {
      const response = await this.api.get(
        `/api/v1/tracking_client/${encodeURIComponent(codigoCliente)}`
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao buscar status do pacote'
      );
    }
  }

  async addTrackingStep(code: string, data: any): Promise<any> {
    try {
      const response = await this.api.post(
        `/api/v1/tracking/${code}/steps`,
        data
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message ||
          'Erro ao adicionar etapa de rastreamento'
      );
    }
  }

  async updateTrackingStep(stepId: number, data: any): Promise<any> {
    try {
      const response = await this.api.patch(
        `/api/v1/tracking/steps/${stepId}`,
        data
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message ||
          'Erro ao atualizar etapa de rastreamento'
      );
    }
  }

  async deleteTrackingStep(stepId: number): Promise<void> {
    try {
      await this.api.delete(`/api/v1/tracking/steps/${stepId}`);
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao excluir etapa de rastreamento'
      );
    }
  }

  // ========== CUSTOS ==========
  async getCosts(params?: {
    search?: string;
    categoria?: string;
    status?: string;
    page?: number;
    per_page?: number;
    data_inicio?: string;
    data_fim?: string;
    sort?: string;
    order?: string;
  }): Promise<any> {
    try {
      const response = await this.api.get('/api/v1/costs', { params });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erro ao buscar custos');
    }
  }

  async createCost(data: any): Promise<any> {
    try {
      const formData = new FormData();
      Object.keys(data).forEach(key => {
        if (key === 'comprovante' && data[key]) {
          if (data[key].file) {
            formData.append('comprovante', data[key].file);
          }
        } else if (data[key] !== null && data[key] !== undefined) {
          formData.append(key, data[key]);
        }
      });

      // Adicionar metodo_pagamento se fornecido
      if (data.metodo_pagamento !== undefined && data.metodo_pagamento !== null) {
        formData.append('metodo_pagamento', String(data.metodo_pagamento));
      }

      const response = await this.api.post('/api/v1/costs', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erro ao criar custo');
    }
  }

  async cancelCost(id: number, justificativa: string): Promise<any> {
    try {
      const response = await this.api.patch(`/api/v1/costs/${id}/cancel`, {
        justificativa_cancelamento: justificativa,
      });
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao cancelar custo'
      );
    }
  }

  async getCostsSummary(): Promise<any> {
    try {
      const response = await this.api.get('/api/v1/costs/summary');
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao buscar resumo de custos'
      );
    }
  }

  // ========== PRODUTOS ==========
  async getProducts(params?: {
    search?: string;
    categoria_id?: number;
    status?: string;
    page?: number;
    per_page?: number;
  }): Promise<any> {
    try {
      const response = await this.api.get('/api/v1/products', { params });
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao buscar produtos'
      );
    }
  }

  async createProduct(data: any): Promise<any> {
    try {
      const response = await this.api.post('/api/v1/products', data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erro ao criar produto');
    }
  }

  async updateProduct(id: number, data: any): Promise<any> {
    try {
      const response = await this.api.patch(`/api/v1/products/${id}`, data);
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao atualizar produto'
      );
    }
  }

  async deleteProduct(id: number): Promise<void> {
    try {
      await this.api.delete(`/api/v1/products/${id}`);
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao excluir produto'
      );
    }
  }

  async getInvoiceCompilacao(invoiceId: number): Promise<any> {
    try {
      const response = await this.api.get(`/api/v1/invoices/${invoiceId}/compilacao`);
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao carregar compilação'
      );
    }
  }

  async createInvoiceCompilacao(invoiceId: number, data: any): Promise<any> {
    try {
      const response = await this.api.post(
        `/api/v1/invoices/${invoiceId}/compilacao`,
        data
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao compilar itens'
      );
    }
  }

  async updateInvoiceCompilacao(linhaId: number, data: any): Promise<any> {
    try {
      const response = await this.api.patch(`/api/v1/compilacao/${linhaId}`, data);
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao atualizar linha compilada'
      );
    }
  }

  async deleteInvoiceCompilacao(linhaId: number): Promise<any> {
    try {
      const response = await this.api.delete(`/api/v1/compilacao/${linhaId}`);
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao excluir linha compilada'
      );
    }
  }

  async downloadInvoiceCompilacaoXls(invoiceId: number, filename?: string): Promise<void> {
    try {
      const response = await this.api.get(
        `/api/v1/invoices/${invoiceId}/compilacao/xls`,
        { responseType: 'blob' }
      );
      const blob = new Blob([response.data], {
        type: 'application/vnd.ms-excel'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename || `invoice-${invoiceId}.xls`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao baixar XLS da invoice'
      );
    }
  }

  async getProductCategories(): Promise<any> {
    try {
      const response = await this.api.get('/api/v1/products/categories');
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao buscar categorias de produtos'
      );
    }
  }

  // ========== PRODUTO (endpoints alternativos) ==========
  async addProduto(data: {
    nome: string;
    descricao: string;
    preco: number;
    categoria_id: number;
    foto?: string;
  }): Promise<any> {
    try {
      const formData = new FormData();
      formData.append('nome', data.nome);
      formData.append('descricao', data.descricao);
      formData.append('preco', String(data.preco));
      formData.append('categoria_id', String(data.categoria_id));
      if (data.foto) {
        formData.append('foto', data.foto);
      }

      const response = await this.api.post('/api/v1/produto/add', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao adicionar produto'
      );
    }
  }

  async listProdutos(): Promise<any> {
    try {
      const response = await this.api.get('/api/v1/produto/list');
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao listar produtos'
      );
    }
  }

  // ========== CONFIGURAÇÕES ==========
  async getSettings(): Promise<any> {
    try {
      const response = await this.api.get('/api/v1/settings');
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao buscar configurações'
      );
    }
  }

  async updateSettings(data: any): Promise<any> {
    try {
      const response = await this.api.patch('/api/v1/settings', data);
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao atualizar configurações'
      );
    }
  }

  async getRoles(): Promise<any> {
    try {
      const response = await this.api.get('/api/v1/settings/roles');
      return response.data;
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        'Erro ao buscar roles';
      throw new Error(errorMessage);
    }
  }

  /** Páginas permitidas do usuário autenticado (Gestão de Roles / ACL) */
  async getMyAccessPages(): Promise<any> {
    try {
      const response = await this.api.get('/api/v1/access/pages');
      return response.data;
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        'Erro ao buscar páginas de acesso';
      throw new Error(errorMessage);
    }
  }

  async createRole(data: {
    nome: string;
    descricao?: string;
    permissaos_id: number;
    paginaList: string[];
  }): Promise<any> {
    try {
      const response = await this.api.post('/api/v1/settings/roles', data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erro ao criar role');
    }
  }

  async updateRole(
    id: number,
    data: {
      nome: string;
      descricao?: string;
      permissaos_id: number;
      paginaList: string[];
    }
  ): Promise<any> {
    try {
      const response = await this.api.put(
        `/api/v1/settings/roles/${id}`,
        data
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao atualizar role'
      );
    }
  }

  async deleteRole(id: number): Promise<void> {
    try {
      await this.api.delete(`/api/v1/settings/roles/${id}`);
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erro ao excluir role');
    }
  }

  // ========== ETIQUETAS ==========
  async getLabel(boxId: number): Promise<any> {
    try {
      const response = await this.api.get(`/api/v1/labels/${boxId}`);
      return response.data;
    } catch (error: any) {
      // Preservar o erro original para que o componente possa tratá-lo adequadamente
      const errorMessage = error.response?.data?.message || error.message || 'Erro ao buscar etiqueta';
      const serverError: any = new Error(errorMessage);
      serverError.response = error.response;
      serverError.status = error.response?.status;
      serverError.isServerError = error.response?.status >= 500;
      throw serverError;
    }
  }

  async verifyCode(code: string): Promise<any> {
    try {
      const response = await this.api.get(`/api/v1/verify`, {
        params: { code },
      });

      // A API retorna { status: "success", data: {...} }
      // Precisamos adaptar para o formato esperado pelo componente { box: {...}, invoice: {...} }
      const apiData = response.data;

      if (apiData.status === 'success' && apiData.data) {
        const data = apiData.data;

        // Extrair status_invoice do objeto status se existir
        const statusInvoice = data.status?.status_invoice || data.status_invoice || data.status;
        const statusId = data.status?.id || null;

        // Mapear os dados da API para o formato esperado pelo componente
        return {
          box: {
            id: data.id,
            codigo: data.codigo || code,
            tipo: data.tipo?.toLowerCase() || 'caixa',
            status: typeof statusInvoice === 'string' ? statusInvoice : (data.status?.status_invoice || 'pedido_criado'),
            peso: data.peso || data.peso_total,
            codigo_rastreio: data.codigo_rastreio || data.codigo_rastreamento,
            data_criacao: data.data_criacao,
            // Adicionar campos que podem não estar na resposta mas são esperados
            cliente_nome: data.cliente_destinatario || data.cliente_nome || 'Não informado',
            dimensoes: data.dimensoes,
            valor_remessa: data.valor_remessa,
            conteudo: data.conteudo,
            observacao: data.observacao,
            historico_status: data.historico_status || [],
          },
          invoice: {
            numero_documento: data.numero_documento,
            codigo_rastreamento: data.codigo_rastreio || data.codigo_rastreamento,
            origem: data.origem,
            destino: data.destino,
            data: data.data_criacao,
            status_id: statusId,
            status_invoice: typeof statusInvoice === 'string' ? statusInvoice : (statusInvoice?.status_invoice || statusInvoice?.nome || statusInvoice?.name || ''),
            cliente_destinatario: data.cliente_destinatario,
            peso_total: data.peso_total,
            codigos_acesso: data.codigos_acesso,
          },
        };
      }

      // Fallback para formato antigo (se a API retornar diretamente { box: {...} })
      return apiData;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao verificar código'
      );
    }
  }

  async checkQrCode(code: string, tipo?: string | null): Promise<any> {
    try {
      const response = await this.api.post('/api/v1/verify/check-qr', {
        code,
        ...(tipo === 'cliente' ? { tipo: 'cliente' } : {}),
      });
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao conferir QR Code'
      );
    }
  }

  // Método para verificar código de acesso
  async verifyAccess(data: {
    numero_documento: string;
    codigo_acesso: string;
  }): Promise<any> {
    try {
      const response = await this.api.post('/api/v1/verify/access', data);

      // A resposta deve trazer os pacotes de cada cliente, com fotos e detalhes
      // Formato esperado: { status: "success", data: { pacotes: [...], clientes: [...] } }
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao verificar código de acesso'
      );
    }
  }

  // Método para reportar ocorrência do pacote (chegou/extraviado/violado)
  async reportPackageOccurrence(data: {
    pacote_id: number;
    numero_documento: string;
    codigo_acesso: string;
    tipo: 'chegou' | 'extraviado' | 'violado';
    descricao?: string;
    fotos?: (File | string)[]; // Arquivos ou base64
  }): Promise<any> {
    try {
      const formData = new FormData();

      formData.append('pacote_id', String(data.pacote_id));
      formData.append('numero_documento', data.numero_documento);
      formData.append('codigo_acesso', data.codigo_acesso);
      formData.append('tipo', data.tipo);

      if (data.descricao) {
        formData.append('descricao', data.descricao);
      }

      // Adicionar fotos se fornecidas
      if (data.fotos && Array.isArray(data.fotos)) {
        data.fotos.forEach((foto: File | string, index: number) => {
          if (foto instanceof File) {
            formData.append(`fotos[${index}]`, foto);
          } else if (typeof foto === 'string') {
            // Se for base64, enviar como string
            formData.append(`fotos[${index}]`, foto);
          }
        });
      }

      const response = await this.api.post('/api/v1/pacote/reportar', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao reportar ocorrência do pacote'
      );
    }
  }

  // Método para verificar pacotes do cliente
  async verifyClientPackages(data: {
    codigo_cliente: string;
    codigo_acesso: string;
    numero_documento: string;
  }): Promise<any> {
    try {
      const response = await this.api.post('/api/v1/pacote/verificar-cliente', data);
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao verificar pacotes do cliente'
      );
    }
  }

  // Método para reportar retirada do pacote
  async reportPackageWithdrawal(data: {
    codigo_cliente: string;
    numero_documento: string;
    codigo_acesso: string;
    foto_saida: File | string;
    foto_assinatura?: File | string;
    foto_documento: File | string;
  }): Promise<any> {
    try {
      const formData = new FormData();

      formData.append('codigo_cliente', data.codigo_cliente);
      formData.append('numero_documento', data.numero_documento);
      formData.append('codigo_acesso', data.codigo_acesso);

      // Adicionar fotos
      if (data.foto_saida instanceof File) {
        formData.append('foto_saida', data.foto_saida);
      } else {
        formData.append('foto_saida', data.foto_saida);
      }

      // foto_assinatura é opcional
      if (data.foto_assinatura) {
        if (data.foto_assinatura instanceof File) {
          formData.append('foto_assinatura', data.foto_assinatura);
        } else {
          formData.append('foto_assinatura', data.foto_assinatura);
        }
      }

      if (data.foto_documento instanceof File) {
        formData.append('foto_documento', data.foto_documento);
      } else {
        formData.append('foto_documento', data.foto_documento);
      }

      const response = await this.api.post('/api/v1/pacote/reportar-retirada', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao reportar retirada do pacote'
      );
    }
  }

  // ========== PAGAMENTOS ==========
  async createPayment(data: {
    pacote_id?: number | null;
    cobranca_id?: string | null;
    valor?: number | string;
    metodo_pagamento_id?: number;
    metodo_pagamento?: string;
    moeda_id?: number;
    data_pagamento?: string;
    comprovante?: File;
    comprovantes?: File[];
  }): Promise<any> {
    try {
      const formData = new FormData();

      if (data.pacote_id !== undefined && data.pacote_id !== null) {
        formData.append('pacote_id', String(data.pacote_id));
      }

      if (data.cobranca_id) {
        formData.append('cobranca_id', data.cobranca_id);
      }

      if (data.valor !== undefined && data.valor !== null) {
        formData.append('valor', String(data.valor));
      }

      if (data.metodo_pagamento_id !== undefined && data.metodo_pagamento_id !== null) {
        formData.append('metodo_pagamento_id', String(data.metodo_pagamento_id));
      }

      // Adicionar metodo_pagamento se fornecido
      if (data.metodo_pagamento !== undefined && data.metodo_pagamento !== null) {
        formData.append('metodo_pagamento', String(data.metodo_pagamento));
      }

      if (data.moeda_id !== undefined && data.moeda_id !== null) {
        formData.append('moeda_id', String(data.moeda_id));
      }

      if (data.data_pagamento) {
        formData.append('data_pagamento', String(data.data_pagamento));
      }

      const files =
        data.comprovantes && data.comprovantes.length > 0
          ? data.comprovantes.filter((f) => f instanceof File)
          : data.comprovante instanceof File
            ? [data.comprovante]
            : [];

      files.forEach((file) => {
        formData.append('comprovantes[]', file);
      });

      // Adicionar tipo como "Entrada" para todos os registros de pagamento
      formData.append('tipo', 'Entrada');

      const response = await this.api.post('/api/v1/pagamento/add', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao registrar pagamento'
      );
    }
  }

  async getPaymentByPackage(pacoteId: number): Promise<any> {
    try {
      const response = await this.api.get(`/api/v1/pagamento/pacote/${pacoteId}`);
      return response.data;
    } catch (error: any) {
      // Se não encontrar pagamento, não é erro crítico
      if (error.response?.status === 404) {
        return null;
      }
      throw new Error(
        error.response?.data?.message || 'Erro ao buscar pagamento do pacote'
      );
    }
  }

  async cancelPayment(data: {
    pacote_id: number;
    justificativa_cancelamento: string;
  }): Promise<any> {
    try {
      const response = await this.api.post('/api/v1/pagamento/cancel', data);
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao cancelar pagamento'
      );
    }
  }

  async getTrackingHistory(rastreioId: number): Promise<any> {
    try {
      const response = await this.api.get(`/api/v1/rastreio/historico/${rastreioId}`);
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao buscar histórico de rastreio'
      );
    }
  }

  async createTrackingHistory(data: {
    invoice_id: number;
    status?: string;
    descricao?: string;
    local_pacote?: string;
    localizacao?: string;
    latitude?: number;
    longitude?: number;
    evento_em?: string;
  }): Promise<any> {
    try {
      const response = await this.api.post('/api/v1/rastreio/historico', data);
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao criar histórico de rastreio'
      );
    }
  }

  async updateTrackingHistory(id: number, data: {
    invoice_id?: number;
    status?: string;
    descricao?: string;
    local_pacote?: string;
    localizacao?: string;
    latitude?: number;
    longitude?: number;
    evento_em?: string;
  }): Promise<any> {
    try {
      const response = await this.api.put(`/api/v1/rastreio/historico/${id}`, data);
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao atualizar histórico de rastreio'
      );
    }
  }

  async deleteTrackingHistory(id: number): Promise<any> {
    try {
      const response = await this.api.delete(`/api/v1/rastreio/historico/${id}`);
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao remover histórico de rastreio'
      );
    }
  }

  // ========== TARIFAS ==========
  async getTarifas(params?: {
    search?: string;
    ativo?: boolean | 0 | 1 | 'all';
    page?: number;
    per_page?: number;
  }): Promise<any> {
    try {
      const response = await this.api.get('/api/v1/tarifas', { params });
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao buscar tarifas'
      );
    }
  }

  async getTarifa(id: number): Promise<any> {
    try {
      const response = await this.api.get(`/api/v1/tarifas/${id}`);
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao buscar tarifa'
      );
    }
  }

  async createTarifa(data: {
    nome: string;
    origem: string;
    destino: string;
    valor: number;
    kg_max: number;
    acrescimento_embalagem?: number;
    ativo?: boolean;
  }): Promise<any> {
    try {
      const response = await this.api.post('/api/v1/tarifas', data);
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao criar tarifa'
      );
    }
  }

  async updateTarifa(
    id: number,
    data: {
      nome?: string;
      origem?: string;
      destino?: string;
      valor?: number;
      kg_max?: number;
      acrescimento_embalagem?: number;
      ativo?: boolean;
    }
  ): Promise<any> {
    try {
      const response = await this.api.put(`/api/v1/tarifas/${id}`, data);
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao atualizar tarifa'
      );
    }
  }

  async toggleTarifaAtivo(id: number): Promise<any> {
    try {
      const response = await this.api.patch(`/api/v1/tarifas/${id}/toggle-ativo`);
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao alterar status da tarifa'
      );
    }
  }

  async deleteTarifa(id: number): Promise<void> {
    try {
      await this.api.delete(`/api/v1/tarifas/${id}`);
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao excluir tarifa'
      );
    }
  }

  // ========== COBRANÇAS PIX ==========
  async getCobrancasList(params?: {
    status?: string;
    search?: string;
    cliente_id?: number;
    pacote_id?: number;
    page?: number;
    per_page?: number;
  }): Promise<any> {
    const response = await this.api.get('/api/v1/cobrancas', { params });
    return response.data;
  }

  async getCobrancaClients(params?: {
    search?: string;
    only_pending?: boolean;
    page?: number;
    per_page?: number;
  }): Promise<any> {
    const response = await this.api.get('/api/v1/cobrancas/clients', { params });
    return response.data;
  }

  async getCobrancaClientPackages(
    clienteId: number,
    params?: { only_pending?: boolean }
  ): Promise<any> {
    const response = await this.api.get(
      `/api/v1/cobrancas/clients/${clienteId}/packages`,
      { params }
    );
    return response.data;
  }

  async getCobranca(id: string): Promise<any> {
    const response = await this.api.get(`/api/v1/cobrancas/${id}`);
    return response.data;
  }

  async createCobranca(data: {
    cliente_id: number;
    pacote_id?: number | null;
    titulo: string;
    descricao?: string;
    valor: number;
    vencimento: string;
  }): Promise<any> {
    const response = await this.api.post('/api/v1/cobrancas', data);
    return response.data;
  }

  async updateCobranca(
    id: string,
    data: {
      cliente_id?: number;
      pacote_id?: number | null;
      titulo?: string;
      descricao?: string;
      valor?: number;
      vencimento?: string;
    }
  ): Promise<any> {
    const response = await this.api.put(`/api/v1/cobrancas/${id}`, data);
    return response.data;
  }

  async sendCobranca(id: string): Promise<any> {
    const response = await this.api.post(`/api/v1/cobrancas/${id}/send`);
    return response.data;
  }

  async updateCobrancaStatus(id: string, status: string): Promise<any> {
    const response = await this.api.put(`/api/v1/cobrancas/${id}/status`, { status });
    return response.data;
  }

  async deleteCobranca(id: string): Promise<any> {
    const response = await this.api.delete(`/api/v1/cobrancas/${id}`);
    return response.data;
  }

  async getCobrancaSettings(): Promise<any> {
    const response = await this.api.get('/api/v1/cobrancas/settings');
    return response.data;
  }

  async updateCobrancaSettings(data: Record<string, unknown>): Promise<any> {
    const response = await this.api.put('/api/v1/cobrancas/settings', data);
    return response.data;
  }

  async getCobrancaPublic(id: string): Promise<any> {
    const response = await this.api.get(`/api/v1/cobrancas/${id}/public`);
    return response.data;
  }

  async getCobrancaRetiradaPublic(codigoCliente: string): Promise<any> {
    try {
      const response = await this.api.get(
        `/api/v1/cobrancas/retirada/${encodeURIComponent(codigoCliente)}`
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao consultar cobrança da retirada'
      );
    }
  }

  async gerarCobrancaRetiradaPublic(codigoCliente: string): Promise<any> {
    try {
      const response = await this.api.post(
        `/api/v1/cobrancas/retirada/${encodeURIComponent(codigoCliente)}/gerar`
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao gerar cobrança PIX'
      );
    }
  }

  async reportCobrancaPayment(id: string): Promise<any> {
    const response = await this.api.post(`/api/v1/cobrancas/${id}/report-payment`);
    return response.data;
  }

  async getCurrencyRates(date?: string): Promise<any> {
    const response = await this.api.get('/api/v1/currency/rates', {
      params: date ? { date } : undefined,
    });
    return response.data;
  }

  async convertCurrency(data: {
    amount: number;
    from?: string;
    to?: string;
    from_currency?: string;
    to_currency?: string;
    date?: string;
  }): Promise<any> {
    try {
      const from = String(data.from_currency || data.from || 'BRL').toUpperCase();
      const to = String(data.to_currency || data.to || '').toUpperCase();
      const response = await this.api.post('/api/v1/currency/convert', {
        amount: data.amount,
        from,
        to,
        from_currency: from,
        to_currency: to,
        ...(data.date ? { date: data.date } : {}),
      });
      return response.data;
    } catch (error: any) {
      const validationMsg = error.response?.data?.errors
        ? Object.values(error.response.data.errors).flat().join(' ')
        : null;
      throw new Error(
        error.response?.data?.message || validationMsg || 'Erro ao converter moeda'
      );
    }
  }

  async listPendenciasFinanceiras(params?: {
    search?: string;
    page?: number;
    per_page?: number;
  }): Promise<any> {
    try {
      const response = await this.api.get('/api/v1/pendencias-financeiras', {
        params,
      });
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao listar pendências financeiras'
      );
    }
  }

  async cancelarLancamentoPendenciaFinanceira(
    id: number,
    justificativa: string
  ): Promise<any> {
    try {
      const response = await this.api.post(
        `/api/v1/pendencias-financeiras/${id}/cancelar-lancamento`,
        { justificativa }
      );
      return response.data;
    } catch (error: any) {
      const validationMsg = error.response?.data?.errors
        ? Object.values(error.response.data.errors).flat().join(' ')
        : null;
      throw new Error(
        error.response?.data?.message ||
          validationMsg ||
          'Erro ao cancelar o lançamento'
      );
    }
  }

  async devolverCargaPendenciaFinanceira(
    id: number,
    justificativa: string
  ): Promise<any> {
    try {
      const response = await this.api.post(
        `/api/v1/pendencias-financeiras/${id}/devolver`,
        { justificativa }
      );
      return response.data;
    } catch (error: any) {
      const validationMsg = error.response?.data?.errors
        ? Object.values(error.response.data.errors).flat().join(' ')
        : null;
      throw new Error(
        error.response?.data?.message ||
          validationMsg ||
          'Erro ao devolver a carga ao cliente'
      );
    }
  }

  async listPendenciasPagamento(params?: {
    search?: string;
    status?: string;
    page?: number;
    per_page?: number;
  }): Promise<any> {
    try {
      const response = await this.api.get('/api/v1/pendencias-pagamento', {
        params,
      });
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao listar pendências de pagamento'
      );
    }
  }

  async listPagamentos(params?: {
    search?: string;
    status?: string;
    page?: number;
    per_page?: number;
    somente_pacotes?: boolean;
  }): Promise<any> {
    try {
      const response = await this.api.get('/api/v1/pagamento/list', { params });
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao listar pagamentos'
      );
    }
  }

  async createPendenciaPagamento(data: {
    pacote_id: number;
    valor_brl: number;
    moeda_local: string;
    observacao?: string;
  }): Promise<any> {
    try {
      const response = await this.api.post('/api/v1/pendencias-pagamento', data);
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao registrar pendência de pagamento'
      );
    }
  }

  async baixarPendenciaPagamento(
    id: number | string,
    data: {
      foto_pagamento: File;
      valor_brl?: number;
      codigo_acesso?: string;
      numero_documento?: string;
      codigo_cliente?: string;
      observacao?: string;
    }
  ): Promise<any> {
    try {
      const formData = new FormData();
      formData.append('foto_pagamento', data.foto_pagamento, data.foto_pagamento.name || 'comprovante.jpg');
      if (data.valor_brl != null && !Number.isNaN(Number(data.valor_brl))) {
        formData.append('valor_brl', String(data.valor_brl));
      }
      if (data.codigo_acesso) formData.append('codigo_acesso', data.codigo_acesso);
      if (data.numero_documento) formData.append('numero_documento', data.numero_documento);
      if (data.codigo_cliente) formData.append('codigo_cliente', data.codigo_cliente);
      if (data.observacao) formData.append('observacao', data.observacao);
      const useRetiradaPublic =
        Boolean(data.codigo_acesso && data.numero_documento && data.codigo_cliente);
      const url = useRetiradaPublic
        ? `/api/v1/pacote/pendencias-pagamento/${id}/baixar`
        : `/api/v1/pendencias-pagamento/${id}/baixar`;
      const response = await this.api.post(url, formData, {
        timeout: 60000,
        transformRequest: [
          (body, headers) => {
            if (body instanceof FormData) {
              if (headers && typeof headers.setContentType === 'function') {
                headers.setContentType(false);
              } else if (headers && typeof headers.delete === 'function') {
                headers.delete('Content-Type');
              } else if (headers) {
                delete headers['Content-Type'];
              }
            }
            return body;
          },
        ],
      });
      return response.data;
    } catch (error: any) {
      const payload = error.response?.data;
      const validationMsg = payload?.errors
        ? Object.values(payload.errors)
            .flat()
            .find((msg): msg is string => typeof msg === 'string' && msg.length > 0)
        : null;
      throw new Error(
        payload?.message ||
          validationMsg ||
          'Erro ao registrar pagamento da pendência'
      );
    }
  }

  async cancelPendenciaPagamento(id: number | string): Promise<any> {
    try {
      const response = await this.api.post(`/api/v1/pendencias-pagamento/${id}/cancel`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erro ao cancelar pendência');
    }
  }

  // ——— SendingTo Rewards ———
  async getRewardsDashboard(): Promise<any> {
    const response = await this.api.get('/api/v1/rewards/manage/dashboard');
    return response.data?.data ?? response.data;
  }

  async getRewardsSettings(): Promise<any> {
    const response = await this.api.get('/api/v1/rewards/manage/settings');
    return response.data?.data ?? response.data;
  }

  async updateRewardsSettings(data: any): Promise<any> {
    const response = await this.api.put('/api/v1/rewards/manage/settings', data);
    return response.data?.data ?? response.data;
  }

  async getRewardsAccounts(params?: Record<string, any>): Promise<any> {
    const response = await this.api.get('/api/v1/rewards/manage/accounts', { params });
    return response.data;
  }

  async getRewardsAccount(id: number): Promise<any> {
    const response = await this.api.get(`/api/v1/rewards/manage/accounts/${id}`);
    return response.data;
  }

  async ensureRewardsAccount(data: { cliente_id: number; referral_code?: string }): Promise<any> {
    const response = await this.api.post('/api/v1/rewards/manage/accounts', data);
    return response.data;
  }

  async adjustRewardsPoints(
    id: number,
    data: { points: number; description: string; type?: string }
  ): Promise<any> {
    const response = await this.api.post(`/api/v1/rewards/manage/accounts/${id}/adjust`, data);
    return response.data;
  }

  async setRewardsVip(id: number, is_vip: boolean): Promise<any> {
    const response = await this.api.post(`/api/v1/rewards/manage/accounts/${id}/vip`, { is_vip });
    return response.data;
  }

  async getRewardsTiers(): Promise<any> {
    const response = await this.api.get('/api/v1/rewards/manage/tiers');
    return response.data?.data ?? response.data;
  }

  async updateRewardsTier(id: number, data: any): Promise<any> {
    const response = await this.api.put(`/api/v1/rewards/manage/tiers/${id}`, data);
    return response.data?.data ?? response.data;
  }

  async getRewardsCampaigns(): Promise<any> {
    const response = await this.api.get('/api/v1/rewards/manage/campaigns');
    return response.data?.data ?? response.data;
  }

  async createRewardsCampaign(data: any): Promise<any> {
    const response = await this.api.post('/api/v1/rewards/manage/campaigns', data);
    return response.data?.data ?? response.data;
  }

  async updateRewardsCampaign(id: number, data: any): Promise<any> {
    const response = await this.api.put(`/api/v1/rewards/manage/campaigns/${id}`, data);
    return response.data?.data ?? response.data;
  }

  async getRewardsCatalog(): Promise<any> {
    const response = await this.api.get('/api/v1/rewards/manage/catalog');
    return response.data?.data ?? response.data;
  }

  async createRewardsCatalog(data: any): Promise<any> {
    const response = await this.api.post('/api/v1/rewards/manage/catalog', data);
    return response.data?.data ?? response.data;
  }

  async updateRewardsCatalog(id: number, data: any): Promise<any> {
    const response = await this.api.put(`/api/v1/rewards/manage/catalog/${id}`, data);
    return response.data?.data ?? response.data;
  }

  async deleteRewardsCatalog(id: number): Promise<any> {
    const response = await this.api.delete(`/api/v1/rewards/manage/catalog/${id}`);
    return response.data?.data ?? response.data;
  }

  async getRewardsMissions(): Promise<any> {
    const response = await this.api.get('/api/v1/rewards/manage/missions');
    return response.data?.data ?? response.data;
  }

  async createRewardsMission(data: any): Promise<any> {
    const response = await this.api.post('/api/v1/rewards/manage/missions', data);
    return response.data?.data ?? response.data;
  }

  async updateRewardsMission(id: number, data: any): Promise<any> {
    const response = await this.api.put(`/api/v1/rewards/manage/missions/${id}`, data);
    return response.data?.data ?? response.data;
  }

  async getRewardsCoupons(): Promise<any> {
    const response = await this.api.get('/api/v1/rewards/manage/coupons');
    return response.data?.data ?? response.data;
  }

  async createRewardsCoupon(data: any): Promise<any> {
    const response = await this.api.post('/api/v1/rewards/manage/coupons', data);
    return response.data?.data ?? response.data;
  }

  async getRewardsRedemptions(params?: Record<string, any>): Promise<any> {
    const response = await this.api.get('/api/v1/rewards/manage/redemptions', { params });
    return response.data;
  }

  async fulfillRewardsRedemption(
    id: number,
    data: { status: string; notes?: string }
  ): Promise<any> {
    const response = await this.api.post(`/api/v1/rewards/manage/redemptions/${id}/fulfill`, data);
    return response.data;
  }

  async getRewardsRanking(params?: { limit?: number; year?: string }): Promise<any> {
    const response = await this.api.get('/api/v1/rewards/manage/ranking', { params });
    return response.data?.data ?? response.data;
  }

  async getRewardsLedger(params?: Record<string, any>): Promise<any> {
    const response = await this.api.get('/api/v1/rewards/manage/ledger', { params });
    return response.data;
  }

  async getRewardsBusinessTiers(): Promise<any> {
    const response = await this.api.get('/api/v1/rewards/manage/business-tiers');
    return response.data?.data ?? response.data;
  }

  // ========== CATÁLOGO (web-cargo) ==========
  private appendCatalogProductForm(data: {
    category?: string;
    model?: string;
    name?: string;
    description?: string;
    price?: number | string;
    sizes?: string[];
    colors?: string[];
    active?: boolean;
    sort_order?: number;
    image?: File | null;
  }): FormData {
    const formData = new FormData();
    if (data.category != null) formData.append('category', String(data.category));
    if (data.model != null) formData.append('model', String(data.model));
    if (data.name != null) formData.append('name', String(data.name));
    if (data.description != null) formData.append('description', String(data.description));
    if (data.price != null && data.price !== '') formData.append('price', String(data.price));
    formData.append('sizes', JSON.stringify(Array.isArray(data.sizes) ? data.sizes : []));
    formData.append('colors', JSON.stringify(Array.isArray(data.colors) ? data.colors : []));
    if (data.active != null) formData.append('active', data.active ? '1' : '0');
    if (data.sort_order != null) formData.append('sort_order', String(data.sort_order));
    if (data.image instanceof File) formData.append('image', data.image);
    return formData;
  }

  async getCatalogProducts(params?: {
    search?: string;
    category?: string;
    active?: boolean | string;
  }): Promise<any> {
    try {
      const response = await this.api.get('/api/v1/catalog/admin/products', { params });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erro ao buscar produtos do catálogo');
    }
  }

  async createCatalogProduct(data: Record<string, any>): Promise<any> {
    try {
      const response = await this.api.post(
        '/api/v1/catalog/admin/products',
        this.appendCatalogProductForm(data),
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      return response.data;
    } catch (error: any) {
      const first =
        error.response?.data?.errors &&
        (Object.values(error.response.data.errors)[0] as string[] | undefined)?.[0];
      throw new Error(first || error.response?.data?.message || 'Erro ao criar produto');
    }
  }

  async updateCatalogProduct(id: number, data: Record<string, any>): Promise<any> {
    try {
      const response = await this.api.post(
        `/api/v1/catalog/admin/products/${id}`,
        this.appendCatalogProductForm(data),
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      return response.data;
    } catch (error: any) {
      const first =
        error.response?.data?.errors &&
        (Object.values(error.response.data.errors)[0] as string[] | undefined)?.[0];
      throw new Error(first || error.response?.data?.message || 'Erro ao atualizar produto');
    }
  }

  async importCatalogPdf(
    file: File,
    category: 'chinelo' | 'calcinha' = 'chinelo',
    deactivatePrevious = true
  ): Promise<any> {
    try {
      const formData = new FormData();
      formData.append('pdf', file);
      formData.append('category', category);
      formData.append('deactivate_previous', deactivatePrevious ? '1' : '0');
      const response = await this.api.post('/api/v1/catalog/admin/products/import-pdf', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 180000,
      });
      return response.data;
    } catch (error: any) {
      const first =
        error.response?.data?.errors &&
        (Object.values(error.response.data.errors)[0] as string[] | undefined)?.[0];
      throw new Error(first || error.response?.data?.message || 'Erro ao importar PDF');
    }
  }

  async deleteCatalogProduct(id: number): Promise<any> {
    try {
      const response = await this.api.delete(`/api/v1/catalog/admin/products/${id}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erro ao excluir produto');
    }
  }

  async getCatalogOrders(params?: {
    status?: string;
    search?: string;
    page?: number;
    per_page?: number;
  }): Promise<any> {
    try {
      const response = await this.api.get('/api/v1/catalog/admin/orders', { params });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erro ao buscar pedidos do catálogo');
    }
  }

  async updateCatalogOrder(id: number, data: { status: string }): Promise<any> {
    try {
      const response = await this.api.patch(`/api/v1/catalog/admin/orders/${id}`, data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erro ao atualizar pedido');
    }
  }

  // ========== EXCLUSÃO DE CONTA (SendingTo Cargo) ==========
  async getAccountDeletionRequests(params?: {
    status?: string;
    search?: string;
    page?: number;
    per_page?: number;
  }): Promise<any> {
    try {
      const response = await this.api.get('/api/v1/account-deletion-requests', { params });
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao buscar solicitações de exclusão'
      );
    }
  }

  async processAccountDeletionRequest(
    id: number,
    data?: { admin_notes?: string }
  ): Promise<any> {
    try {
      const response = await this.api.post(
        `/api/v1/account-deletion-requests/${id}/process`,
        data || {}
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao processar exclusão de conta'
      );
    }
  }

  async rejectAccountDeletionRequest(
    id: number,
    data?: { admin_notes?: string }
  ): Promise<any> {
    try {
      const response = await this.api.post(
        `/api/v1/account-deletion-requests/${id}/reject`,
        data || {}
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao rejeitar solicitação'
      );
    }
  }

  // ========== CAIXAS FÍSICAS (ORGANIZAR CAIXA) ==========
  async getCaixaFisicaBoard(invoiceId: number): Promise<any> {
    try {
      const response = await this.api.get('/api/v1/caixas-fisicas/board', {
        params: { invoice_id: invoiceId },
      });
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao carregar organização das caixas'
      );
    }
  }

  async createCaixaFisica(data: {
    invoice_id: number;
    numero?: string;
    ocupacao?: 'exclusivo' | 'misturado';
    observacao?: string;
    pacote_ids?: number[];
  }): Promise<any> {
    try {
      const response = await this.api.post('/api/v1/caixas-fisicas', data);
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao criar caixa física'
      );
    }
  }

  async updateCaixaFisica(
    id: number,
    data: {
      numero?: string;
      ocupacao?: 'exclusivo' | 'misturado';
      observacao?: string;
    }
  ): Promise<any> {
    try {
      const response = await this.api.patch(`/api/v1/caixas-fisicas/${id}`, data);
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao atualizar caixa física'
      );
    }
  }

  async deleteCaixaFisica(id: number): Promise<any> {
    try {
      const response = await this.api.delete(`/api/v1/caixas-fisicas/${id}`);
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao excluir caixa física'
      );
    }
  }

  async attachCaixaFisicaPacotes(
    caixaId: number,
    pacoteIds: number[],
    observacao?: string
  ): Promise<any> {
    try {
      const response = await this.api.post(
        `/api/v1/caixas-fisicas/${caixaId}/pacotes`,
        { pacote_ids: pacoteIds, observacao: observacao || null }
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao atribuir CX à caixa física'
      );
    }
  }

  async updateCaixaFisicaPacote(
    caixaId: number,
    pacoteId: number,
    observacao?: string | null
  ): Promise<any> {
    try {
      const response = await this.api.patch(
        `/api/v1/caixas-fisicas/${caixaId}/pacotes/${pacoteId}`,
        { observacao: observacao ?? null }
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao atualizar observação do CX'
      );
    }
  }

  async detachCaixaFisicaPacote(caixaId: number, pacoteId: number): Promise<any> {
    try {
      const response = await this.api.delete(
        `/api/v1/caixas-fisicas/${caixaId}/pacotes/${pacoteId}`
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Erro ao remover CX da caixa física'
      );
    }
  }
}

// Instância única do serviço
export const apiService = new ApiService();

export default apiService;
